import { DeleteIcon } from 'cokitchen-icons';
import React, { useEffect, useRef, useState } from 'react';

import CurrencyInput from 'components/base/currency-input';
import SelectDropdown from 'components/base/select-dropdown';

type BatchOptionsType = {
  id: string;
  value: string;
  name: string;
  price: string;
  quantity: number;
}[];

type SelectedBatch = {
  batch_no: string;
  id: string;
  excess_quantity: string;
  quantity_wasted: string;
};

const PrepReportItem = ({
  item,
  onChange
}: {
  item: any;
  onChange: (arg0: SelectedBatch[]) => void;
}) => {
  const [batches, setBatches] = useState<BatchOptionsType>([]);
  const [availableBatches, setAvailableBatches] = useState<BatchOptionsType>([]);
  const [selectedBatches, setSelectedBatches] = useState<SelectedBatch[]>([
    {
      batch_no: '0',
      id: '',
      excess_quantity: item.excess_quantity ? String(item.excess_quantity) : '',
      quantity_wasted: item.quantity_wasted ? String(item.quantity_wasted) : ''
    }
  ]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!item.batches.length) return;
    setBatches(
      item.batches.map((batch: { id: string; batch_no: number; quantity: number }) => ({
        value: batch.id,
        name: String(batch.batch_no),
        id: batch.id,
        price: `${batch.quantity.toString()} grams`,
        quantity: Number(batch.quantity || 0)
      }))
    );
  }, []);

  useEffect(() => {
    const aBatches = batches.filter((batch) =>
      selectedBatches.every((item) => item.id !== batch.id)
    );
    setAvailableBatches(aBatches);
  }, [batches, selectedBatches]);

  useEffect(() => {
    if (batches.length === 0) return;
    setSelectedBatches([
      {
        id: batches[0]?.id,
        batch_no: String(batches[0]?.name),
        excess_quantity: item.excess_quantity ? String(item.excess_quantity) : '',
        quantity_wasted: item.quantity_wasted ? String(item.quantity_wasted) : ''
      }
    ]);
  }, [batches]);

  useEffect(() => {
    onChangeRef.current(selectedBatches);
  }, [selectedBatches]);

  const addNewBatch = () => {
    if (availableBatches.length === 0) return;

    setSelectedBatches((prev) => [
      ...prev,
      {
        id: availableBatches[0].id,
        batch_no: String(availableBatches[0].name),
        excess_quantity: '',
        quantity_wasted: ''
      }
    ]);

    setAvailableBatches((prev) =>
      prev.filter((batch) => batch.value !== availableBatches[0].value)
    );
  };

  const updateSelectedBatch = (index: number, updates: Partial<SelectedBatch>) => {
    const currentBatch = batches.find((batch) => batch.id === selectedBatches[index]?.id);
    const maxQuantity = currentBatch?.quantity ?? 0;
    const unit = item.ingredient?.unit_type ?? 'grams';
    const excessKey = `${index}-excess`;
    const wastedKey = `${index}-wasted`;

    const currentExcess = Number(selectedBatches[index].excess_quantity || 0);
    const currentWasted = Number(selectedBatches[index].quantity_wasted || 0);

    if (updates.excess_quantity !== undefined) {
      const val = Number(updates.excess_quantity || 0);
      const available = maxQuantity - currentWasted;
      if (val > available) {
        setErrors((prev) => ({
          ...prev,
          [excessKey]: `Cannot exceed ${available} ${unit} (${maxQuantity} - ${currentWasted} wasted).`
        }));
        return;
      }
      setErrors((prev) => {
        const next = { ...prev };
        delete next[excessKey];
        return next;
      });
    }

    if (updates.quantity_wasted !== undefined) {
      const val = Number(updates.quantity_wasted || 0);
      const available = maxQuantity - currentExcess;
      if (val > available) {
        setErrors((prev) => ({
          ...prev,
          [wastedKey]: `Cannot exceed ${available} ${unit} (${maxQuantity} - ${currentExcess} excess).`
        }));
        return;
      }
      setErrors((prev) => {
        const next = { ...prev };
        delete next[wastedKey];
        return next;
      });
    }

    const updatedItems = [...selectedBatches];
    updatedItems[index] = {
      ...updatedItems[index],
      ...updates
    };
    setSelectedBatches(updatedItems);
  };

  const removeBatch = (index: number) => {
    setSelectedBatches((prev) => prev.filter((_, idx) => idx !== index));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${index}-excess`];
      delete next[`${index}-wasted`];
      return next;
    });
  };

  return (
    <div>
      <div className='flex items-center gap-2 mx-4 text-sm mt-4 font-semibold'>
        <p>{item.ingredient.name}</p>
        <span className='w-1 h-1 rounded-full bg-[#D9D9D9]'></span>

        <p className='font-[400]'>
          {item.quantity_requested} {item.ingredient.unit_type ?? 'units'}
        </p>
      </div>
      <div className='text-xs gap-x-[28px] gap-y-6  border-b border-[#EDEDEE] pb-2 mt-4'>
        {selectedBatches.map((b, index) => {
          const currentBatch = batches.find((batch) => batch.id === b.id);
          const maxQuantity = currentBatch?.price || '0';
          return (
            <div
              key={index}
              className={`grid grid-cols-2 gap-4 mt-4 mx-4  ${
                item.batches?.length === 0 && 'opacity-50 cursor-not-allowed'
              }`}>
              <CurrencyInput
                label='Excess Qty (optional)'
                disabled={item.batches?.length === 0}
                onChange={(value) => updateSelectedBatch(index, { excess_quantity: value })}
                name='excess_quantity'
                placeholder='500'
                value={b.excess_quantity}
                disableUnit
                hideCurrency
                error={errors[`${index}-excess`]}
              />
              <CurrencyInput
                label='Qty wasted'
                name='quantity_wasted'
                placeholder='0'
                disabled={item.batches?.length === 0}
                value={b.quantity_wasted}
                onChange={(value) => updateSelectedBatch(index, { quantity_wasted: value })}
                disableUnit
                hideCurrency
                error={errors[`${index}-wasted`]}
              />

              <div className='w-full'>
                <SelectDropdown
                  label='Batch'
                  placeholder='Select batch'
                  value={'Batch' + ' ' + b?.batch_no}
                  setValue={(val) => {
                    updateSelectedBatch(index, { batch_no: val.name.split(' ')[1], id: val.id });
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next[`${index}-excess`];
                      delete next[`${index}-wasted`];
                      return next;
                    });
                  }}
                  options={availableBatches.map((batch) => ({
                    ...batch,
                    name: `Batch ${batch.name}`
                  }))}
                />
                <p className='text-xs text-[#96999C] mt-1'>
                  <span className='text-[#0080E5]'> Maximum Quantity: </span> {maxQuantity}
                </p>
              </div>

              <button type='button' onClick={() => removeBatch(index)} className='w-6'>
                <DeleteIcon className='self-center text-red-5 cursor-pointer w-5 h-5' />
              </button>
            </div>
          );
        })}
        <div className='flex justify-end mt-2'>
          {availableBatches.length > 0 && (
            <button onClick={addNewBatch} className='text-[#0080E5]'>
              + Add new batch
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrepReportItem;
